import { ACCOUNT_ACTION, AUTH_ACTION } from '../../../constants/actions'
import { DEFAULT_COUNTRY_VALUE } from '../../../constants/app'
import {
  ACCOUNT_INIT_PROCESS_STEP,
  DEFAULT_CONTAINER_STATE
} from '../../../constants/containerStates'
import { COGNITO_ERRORS } from '../../../constants/errors'
import { ERROR_TEXT, MESSAGE_TEXT } from '../../../constants/messages'
import {
  extractInputValueAndError,
  mergeToNewObject
} from '../../../util/object.helpers'
import { handleUpdateUserErrors } from '../../../util/auth.helpers'

export const accountProfile = (state = INITIAL_STATE, { type, payload }) => {
  switch (type) {
    case ACCOUNT_ACTION.CHANGE_STATE:
      return mergeToNewObject(state, { [payload.property]: payload.value })

    case ACCOUNT_ACTION.GET_ACCOUNT_DETAILS:
      return mergeToNewObject(state, { loading: true })

    case ACCOUNT_ACTION.CHANGE_INPUT_AND_ERROR_STATE:
      return mergeToNewObject(
        state,
        extractInputValueAndError(payload, state.error)
      )

    case ACCOUNT_ACTION.GET_ACCOUNT_DETAILS_SUCCESS:
    case ACCOUNT_ACTION.REFRESH_KYC_SUCCESS:
    case ACCOUNT_ACTION.ON_CREATE_ACCOUNT_SUCCESS:
      return mergeToNewObject(state, handleGetAccountDetailsResponse(payload), {
        loading: false
      })

    case ACCOUNT_ACTION.GET_ACCOUNT_DETAILS_FAILED:
      return mergeToNewObject(state, {
        loading: false
      })

    case ACCOUNT_ACTION.ON_UPDATE_USER_ACCOUNT_SUCCESS:
      return mergeToNewObject(state, handleUserUpdateResponse(payload, state), {
        isProcessing: false
      })

    case AUTH_ACTION.ON_UPDATE_USER_ATTRIBUTES:
    case ACCOUNT_ACTION.ON_UPDATE_USER_ACCOUNT:
      return mergeToNewObject(state, { isProcessing: true })

    case AUTH_ACTION.EMAIL_CHANGE_SUCCESS:
      return mergeToNewObject(
        state,
        {
          emailTemp: state.email,
          success: {
            emailTemp: buildSuccessText('emailTemp')
          }
        },
        { loading: false }
      )

    case AUTH_ACTION.EMAIL_CHANGE_FAILED:
    case AUTH_ACTION.ON_UPDATE_USER_ATTRIBUTES_FAILED:
      return mergeToNewObject(
        state,
        { error: handleUpdateUserErrors(payload) },
        { isProcessing: false }
      )

    case AUTH_ACTION.ON_CHANGE_PASSWORD_SUCCESS:
      return mergeToNewObject(
        state,
        { success: { newPassword: MESSAGE_TEXT.PASSWORD_CHANGED } },
        { isProcessing: false }
      )

    case AUTH_ACTION.ON_CHANGE_PASSWORD_FAILED:
      return mergeToNewObject(
        state,
        { error: handleChangePasswordErrors(payload) },
        { isProcessing: false }
      )

    case ACCOUNT_ACTION.RESET_STATE:
      return mergeToNewObject(state, handleResetState(state))

    case ACCOUNT_ACTION.ON_CREATE_ACCOUNT_FAILED:
      // case ACCOUNT_ACTION.ON_GET_USER_COUNTRY_FAILED:
      return mergeToNewObject(state, {
        accountInitState: ACCOUNT_INIT_PROCESS_STEP.CREATE_ACCOUNT_FAILED
      })

    default:
      return state
  }
}

const INITIAL_STATE = {
  userId: '',
  authId: '',

  firstName: '',
  firstNameTemp: '',

  lastName: '',
  lastNameTemp: '',

  email: '',
  emailTemp: '',

  oldPassword: '',
  newPassword: '',

  language: '',

  country: DEFAULT_COUNTRY_VALUE,
  countryTemp: DEFAULT_COUNTRY_VALUE,

  accountInitState: DEFAULT_CONTAINER_STATE.NONE,

  error: {},
  success: {},

  insertedAt: '',

  loading: false,
  isProcessing: false
}

export const ACCOUNT_PROFILE_DISPATCH = {
  AUTH_ID: 'authId',

  FIRST_NAME: 'firstName',
  FIRST_NAME_TEMP: 'firstNameTemp',

  LAST_NAME: 'lastName',
  LAST_NAME_TEMP: 'lastNameTemp',

  EMAIL: 'email',
  EMAIL_TEMP: 'emailTemp',

  OLD_PASSWORD: 'oldPassword',
  NEW_PASSWORD: 'newPassword',

  LANGUAGE: 'language',

  ACCOUNT_INIT_STATE: 'accountInitState',

  COUNTRY: 'country',
  COUNTRY_TEMP: 'countryTemp',

  ERROR: 'error',
  SUCCESS: 'success',
  LOADING: 'loading'
}

function handleGetAccountDetailsResponse(payload) {
  const {
    firstName,
    secondName,
    email,
    id,
    language,
    authId,
    countryISO,
    insertedAt
  } = payload
  return {
    userId: id,

    authId,

    firstName,
    firstNameTemp: firstName,

    lastName: secondName,
    lastNameTemp: secondName,

    email,
    emailTemp: email,

    language,
    country: countryISO,
    countryTemp: countryISO,

    insertedAt,

    error: {},
    success: {}
  }
}

function handleResetState(state) {
  const { firstName, lastName, email, country, insertedAt } = state

  return {
    firstNameTemp: firstName,
    lastNameTemp: lastName,
    emailTemp: email,
    oldPassword: '',
    newPassword: '',
    countryTemp: country,
    insertedAt,
    error: {},
    success: {}
  }
}

const handleUserUpdateResponse = (payload, state) => {
  const { firstName, secondName, email, language, countryISO } = payload
  const {
    firstName: firstNameTemp,
    lastName: lastNameTemp,
    country: countryTemp
  } = state
  const oldUser = { firstNameTemp, lastNameTemp, countryTemp }
  const updatedUser = {
    firstNameTemp: firstName,
    lastNameTemp: secondName,
    countryTemp: countryISO
  }
  const success = Object.getOwnPropertyNames(updatedUser).reduce(
    (curr, nextProperty) => {
      if (updatedUser[nextProperty] !== oldUser[nextProperty]) {
        return { ...curr, [nextProperty]: buildSuccessText(nextProperty) }
      } else {
        return curr
      }
    },
    {}
  )
  return {
    firstName,
    lastName: secondName,
    email,
    emailTemp: email,
    language,
    country: countryISO,
    success,
    error: {}
  }
}

const buildSuccessText = (field) => {
  switch (field) {
    case ACCOUNT_PROFILE_DISPATCH.FIRST_NAME_TEMP:
      return MESSAGE_TEXT.FIRST_NAME_CHANGED
    case ACCOUNT_PROFILE_DISPATCH.LAST_NAME_TEMP:
      return MESSAGE_TEXT.LAST_NAME_CHANGED
    case ACCOUNT_PROFILE_DISPATCH.EMAIL_TEMP:
      return MESSAGE_TEXT.VERIFICATION_EMAIL_SENT
    case ACCOUNT_PROFILE_DISPATCH.COUNTRY_TEMP:
      return MESSAGE_TEXT.COUNTRY_CHANGED
    default:
      return ''
  }
}

const handleChangePasswordErrors = ({ code }) => {
  switch (code) {
    case COGNITO_ERRORS.NOT_AUTHORIZED_EXCEPTION:
      return {
        oldPassword: ERROR_TEXT.INCORRECT_OLD_PASSWORD
      }
    case COGNITO_ERRORS.LIMIT_EXCEEDED_EXCEPTION:
      return {
        newPassword: ERROR_TEXT.ATTEMPT_LIMIT_EXCEEDED
      }
    default:
      return {
        newPassword: ERROR_TEXT.SOMETHING_WENT_WRONG
      }
  }
}
